'use client'

import { createTranslator } from 'next-intl'
import { useMemo, useSyncExternalStore } from 'react'

import { useAppLocale } from '@/components/i18n/locale-provider'
import { useInheritedMessages } from '@/components/i18n/messages-provider'
import {
  DEFAULT_LOCALE,
  type Locale,
  localeFromLanguageTag,
} from '@/lib/i18n/config'
import englishErrorPage from '@/messages/en/errorPage.json'

type ErrorPageMessages = typeof englishErrorPage

/** The catalog loaded for the page's language, once it arrives. */
type LoadedCatalog = { locale: Locale; messages: ErrorPageMessages }

let loadedCatalog: LoadedCatalog | null = null
let requestedLocale: Locale | null = null
const listeners = new Set<() => void>()

/**
 * The page's language: the marketing root writes it into `<html lang>`, a
 * product root sets it once the visitor's arrives (`DocumentLanguage`).
 */
function readDocumentLocale(): Locale {
  return localeFromLanguageTag(document.documentElement.lang) ?? DEFAULT_LOCALE
}

/** Starts loading the page language's catalog, once per language. */
function loadDocumentCatalog() {
  const locale = readDocumentLocale()
  if (locale === DEFAULT_LOCALE || requestedLocale === locale) {
    return
  }
  requestedLocale = locale
  import('@/components/i18n/standalone-catalogs')
    .then((catalogs) => catalogs.loadErrorPageCatalog(locale))
    .then((messages) => {
      loadedCatalog = { locale, messages }
      for (const listener of listeners) {
        listener()
      }
    })
    .catch(() => {
      // English stays: a missing chunk must not break the error page.
    })
}

/**
 * Loads the page language's catalog on the first subscriber, and again
 * when the language arrives after the boundary showed (a product root's
 * `DocumentLanguage` streams in beside the page that failed).
 */
function subscribe(onChange: () => void): () => void {
  listeners.add(onChange)
  loadDocumentCatalog()
  const observer = new MutationObserver(loadDocumentCatalog)
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['lang'],
  })
  return () => {
    listeners.delete(onChange)
    observer.disconnect()
  }
}

/** Under an inherited catalog nothing loads. */
function subscribeToNothing(): () => void {
  return () => undefined
}

function getLoadedCatalog(): LoadedCatalog | null {
  return loadedCatalog
}

function getServerCatalog(): null {
  return null
}

/**
 * The error boundaries' copy: in the reader's language where an enclosing
 * `ClientMessages` provides `errorPage` (the marketing root, the app and
 * onboarding shells), else the page language's own catalog, loaded when
 * the boundary first shows (the unsubscribe, domain-share and OAuth roots,
 * whose catalogs live in the page that failed); English until it arrives,
 * and when it cannot. A boundary must render wherever it catches, so it
 * never calls `useTranslations`, which throws without a provider.
 */
export function useErrorPageCopy() {
  const translated = useInheritedMessages()?.errorPage as
    | ErrorPageMessages
    | undefined
  const locale = useAppLocale()
  const loaded = useSyncExternalStore(
    translated ? subscribeToNothing : subscribe,
    getLoadedCatalog,
    getServerCatalog
  )
  return useMemo(() => {
    let shown: Locale = DEFAULT_LOCALE
    let messages = englishErrorPage
    if (translated) {
      shown = locale
      messages = translated
    } else if (loaded) {
      shown = loaded.locale
      messages = loaded.messages
    }
    return createTranslator({
      locale: shown,
      messages: { errorPage: messages },
      namespace: 'errorPage',
    })
  }, [locale, translated, loaded])
}
